import { useContext,useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  FILTER_LEAVE_TYPE_KEY,
  FILTER_LOCATION_KEY,
  FILTER_MANAGER_KEY,
  FILTER_SHIFT_PATTERN_KEY,
  FILTER_WORK_CENTERS_KEY
}
  from "../../../assets/constants";
import { StoreType } from "../../../utils/types";
import { Filter } from "../../filters/filter-accordion/types";
import { FilterContext } from "../../filters/filter-context";
import { getTimeOffBalancesFilters, getTimeOffBalancesReport, updateBalance } from "../store/time-off-balances-report-slice";
import { TimeOffBalance } from "../store/time-off-balances-report-types";
import useTimeOffBalancesReportData from "./use-time-off-balances-report-data";


const useTimeOffBalancesReportController = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const attendanceManagementEnabledLocations = useSelector(
      (state: StoreType) => state.user.userData.attendanceManagementEnabledLocations
    );
    const { balances, getReportStatus, filters: filterOptions, getFiltersStatus } = useTimeOffBalancesReportData();
    const [reportData, setReportData] = useState<TimeOffBalance[]>(balances);
    const [searchText, setSearchText] = useState<string>("");

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRowForEdit, setSelectedRowForEdit] = useState<TimeOffBalance | null>(null);

  const handleEditClick = (row: TimeOffBalance) => {
    setSelectedRowForEdit(row);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedRowForEdit(null);
  };

  // Mock confirm action
  const handleUpdateConfirm = (payload: any) => {
    dispatch(updateBalance(payload));
  };

    const filterContext = useContext(FilterContext);
    if (!filterContext) {
        throw new Error("useTimeOffBalancesReportController must be used within a FilterProvider");
    }
    const { appliedFilters,filters, open, setFilterOpen, setFilters, setDataRelations, dataRelations, setIsLoading } = filterContext;

    // Fetch both report data and filter options on component load
    useEffect(() => {
        dispatch(getTimeOffBalancesReport());
        dispatch(getTimeOffBalancesFilters());
    }, [dispatch]);

    // Prepare filter data for the UI once options are fetched
    useEffect(() => {
        if (getFiltersStatus === "pending") {
            setIsLoading?.(true);
        } else if (getFiltersStatus === "fulfilled") {
            setIsLoading?.(false);
            const enabledLocationIds = attendanceManagementEnabledLocations || [];
            const filteredLocations = enabledLocationIds.length > 0
                ? filterOptions.locations.filter((loc: any) => enabledLocationIds.includes(loc.id))
                : filterOptions.locations;

            const newFilters: Filter[] = [
              {
                id: 1,
                name: t("time-off-balances_location"),
                key: FILTER_LOCATION_KEY,
                data: filteredLocations,
                reducedData: filteredLocations,
                searchedData: filteredLocations,
              },
              {
                id: 2,
                name: t("time-off-balances_work-center"),
                key: FILTER_WORK_CENTERS_KEY,
                data: filterOptions.workcenters,
                reducedData: filterOptions.workcenters,
                searchedData: filterOptions.workcenters,
              },
              {
                id: 3,
                name: t("time-off-balances_shift-pattern"),
                key: FILTER_SHIFT_PATTERN_KEY,
                data: filterOptions.shiftPatterns,
                reducedData: filterOptions.shiftPatterns,
                searchedData: filterOptions.shiftPatterns,
              },
              {
                id: 4,
                name: t("time-off-balances_time-off-type"),
                key: FILTER_LEAVE_TYPE_KEY,
                data: filterOptions.timeOffTypes,
                reducedData: filterOptions.timeOffTypes,
                searchedData: filterOptions.timeOffTypes,
              },
              {
                id: 5,
                name: t("time-off-balances_reporting-manager"),
                key: FILTER_MANAGER_KEY,
                data: filterOptions.reportingManagers,
                reducedData: filterOptions.reportingManagers,
                searchedData: filterOptions.reportingManagers,
              },
            ];
            setFilters(newFilters);
            setDataRelations(filterOptions?.filterRelationships)
        } else if (getFiltersStatus === "error") {
            setIsLoading?.(false);
        }
    }, [getFiltersStatus, filterOptions, setFilters, setDataRelations, attendanceManagementEnabledLocations, setIsLoading, t]);

    // Apply selected filters and search text to the data
    useEffect(() => {
        let filteredData = [...balances];

        if (appliedFilters.length > 0) {
            filteredData = filteredData.filter(row => {
                return appliedFilters.every(filter => {
                    if (filter.data.length === 0) return true;
                    const selectedNames = filter.data.map(item => item.name);
                    switch (filter.key) {
                        case FILTER_LOCATION_KEY:
                            return row.location && selectedNames.includes(row.location);
                        case FILTER_WORK_CENTERS_KEY:
                            return row.workcenter && selectedNames.includes(row.workcenter);
                        case FILTER_SHIFT_PATTERN_KEY:
                            return row.shiftPattern && selectedNames.includes(row.shiftPattern);
                        case FILTER_LEAVE_TYPE_KEY:
                            return row.timeOffType && selectedNames.includes(row.timeOffType);
                        case FILTER_MANAGER_KEY:
                            return row.reportingManager && selectedNames.includes(row.reportingManager);
                        default:
                            return true;
                    }
                });
            });
        }
        
        if (searchText) {
            const lowercasedSearchText = searchText.toLowerCase().replace(/ /g, "");
            filteredData = filteredData.filter((row: TimeOffBalance) => {
                const searchPattern = [
                    row.companyEmployeeId,
                    row.badgeNumber,
                    row.fullName,
                    row.timeOffType,
                    row.balanceFormatted, // Use the pre-formatted value for searching
                    row.jobTitle,
                    row.workcenter,
                    row.location,
                    row.shiftPattern,
                    row.reportingManager,
                    row.badgeNumber,
                ].join(" ").toLowerCase().replace(/ /g, "");
                return searchPattern.includes(lowercasedSearchText);
            });
        }

        setReportData(filteredData);
    }, [balances, appliedFilters, searchText, attendanceManagementEnabledLocations, filterOptions.locations]);


    return [
        { reportData, searchText, getReportStatus, filters, open, editDialogOpen,
          selectedRowForEdit,
          filterOptions,
          dataRelations },
        { setSearchText, setFilters, setFilterOpen, handleEditClick,
          handleEditClose,
          handleUpdateConfirm },
    ] as const;
};

export default useTimeOffBalancesReportController;